
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Image as ImageIcon, Search, Filter, Eye, Loader2, RefreshCw, Archive, Sparkles } from 'lucide-react';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import ImagePopup from './ImagePopup';
import JSZip from 'jszip';

interface GeneratedImageRecord {
  id: string;
  prompt: string;
  image_url: string;
  b64_json?: string;
  model: string;
  size: string;
  quality: string;
  style: string;
  created_at: string;
  batch_id: string;
  original_settings?: any;
  is_enhanced?: boolean;
  enhanced_image_url?: string;
}

interface AllGeneratedImagesProps {
  onCopySettings?: (settings: any) => void;
}

const AllGeneratedImages = ({ onCopySettings }: AllGeneratedImagesProps) => {
  const { user } = useSecureAuthContext();
  const [images, setImages] = useState<GeneratedImageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [modelFilter, setModelFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [hasMore, setHasMore] = useState(true);
  const [isDownloadingIndividual, setIsDownloadingIndividual] = useState(false);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [bulkEnhancing, setBulkEnhancing] = useState(false);
  
  const itemsPerPage = 12; // Reduced for faster initial load
  const DEFAULT_DAYS_BACK = 90;

  useEffect(() => {
    if (user?.id) {
      fetchImages(true);
    } else {
      setLoading(false);
    }
  }, [user?.id, modelFilter, sortBy]);

  const fetchImages = async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setImages([]);
        setHasMore(true);
        setCursor(null);
      } else {
        setLoadingMore(true);
      }

      if (!user?.id) {
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      const since = new Date(Date.now() - DEFAULT_DAYS_BACK * 24 * 60 * 60 * 1000).toISOString();
      let query = supabase
        .from('generated_images')
        .select('id, prompt, image_url, model, size, quality, style, created_at, batch_id, is_enhanced, enhanced_image_url')
        .eq('user_id', user.id)
        .not('image_url', 'is', null)
        .gte('created_at', since)
        .order('created_at', { ascending: sortBy === 'oldest' })
        .limit(itemsPerPage);

      if (modelFilter !== 'all') {
        query = query.eq('model', modelFilter);
      }

      // Keyset pagination to avoid expensive offsets
      if (!reset && cursor) {
        if (sortBy === 'newest') {
          query = query.lt('created_at', cursor);
        } else {
          query = query.gt('created_at', cursor);
        }
      }

      const { data, error } = await query;

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      if (data) {
        const validImages = data.filter(img => 
          img.image_url && 
          img.image_url.trim() !== '' && 
          img.image_url !== 'null' && 
          img.image_url !== 'undefined'
        );

        if (reset) {
          setImages(validImages);
        } else {
          setImages(prev => [...prev, ...validImages]);
        }

        if (validImages.length > 0) {
          const lastItem = validImages[validImages.length - 1];
          setCursor(lastItem.created_at);
        }

        setHasMore(validImages.length === itemsPerPage);
      } else {
        setHasMore(false);
      }

    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Failed to load images');
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setRefreshing(false);
    }
  };

  const refreshImages = async () => {
    setRefreshing(true);
    await fetchImages(true);
  };

  const loadMore = async () => {
    if (!loadingMore && hasMore && !loading) {
      await fetchImages(false);
    }
  };

  const filteredImages = images.filter(image => 
    image.prompt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const downloadImage = async (imageUrl: string, prompt: string) => {
    try {
      if (!imageUrl || imageUrl.trim() === '') {
        toast.error('No image URL available for download');
        return;
      }

      if (imageUrl.startsWith('data:image/')) {
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = `${prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '_')}-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        try {
          const response = await fetch(imageUrl, { mode: 'cors' });
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '_')}-${Date.now()}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (fetchError) {
          window.open(imageUrl, '_blank');
          toast.success('Image opened in new tab');
          return;
        }
      }
      
      toast.success('Image downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image');
    }
  };

  const downloadAllImagesIndividually = async () => {
    if (filteredImages.length === 0) {
      toast.error('No images to download');
      return;
    }

    setIsDownloadingIndividual(true);
    try {
      for (let i = 0; i < filteredImages.length; i++) {
        const image = filteredImages[i];
        await downloadImage(image.image_url, image.prompt);
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (i % 5 === 0 || i === filteredImages.length - 1) {
          toast.success(`Downloaded ${i + 1}/${filteredImages.length} images...`);
        }
      }
      
      toast.success(`All ${filteredImages.length} images downloaded individually!`);
    } catch (error) {
      toast.error('Failed to download some images');
    } finally {
      setIsDownloadingIndividual(false);
    }
  };

  const downloadAllImagesAsZip = async () => {
    if (filteredImages.length === 0) {
      toast.error('No images to download');
      return;
    }

    setIsDownloadingZip(true);
    try {
      const zip = new JSZip();
      const folder = zip.folder('generated-images');
      
      for (let i = 0; i < filteredImages.length; i++) {
        const image = filteredImages[i];
        
        try {
          let imageBlob: Blob;
          
          if (image.image_url.startsWith('data:image/')) {
            // Handle base64 data URLs directly
            const base64Data = image.image_url.split(',')[1];
            const mimeType = image.image_url.match(/data:([^;]+)/)?.[1] || 'image/png';
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let j = 0; j < binaryString.length; j++) {
              bytes[j] = binaryString.charCodeAt(j);
            }
            imageBlob = new Blob([bytes], { type: mimeType });
          } else {
            // Fetch image from URL
            const response = await fetch(image.image_url, { mode: 'cors' });
            imageBlob = await response.blob();
          }
          
          const fileName = `${image.prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '_')}-${image.id.slice(0, 8)}.png`;
          folder?.file(fileName, imageBlob);
          
          if (i % 10 === 0) {
            toast.success(`Processing ${i + 1}/${filteredImages.length} images...`);
          }
        } catch (error) {
          console.error(`Failed to process image ${i + 1}:`, error);
        }
      }
      
      toast.success('Creating ZIP file...');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(zipBlob);
      link.download = `generated-images-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      
      toast.success(`ZIP file with ${filteredImages.length} images downloaded successfully!`);
    } catch (error) {
      console.error('ZIP creation error:', error);
      toast.error('Failed to create ZIP file');
    } finally {
      setIsDownloadingZip(false);
    }
  };

  const toggleImageSelection = (imageId: string) => {
    setSelectedImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  };

  const enhanceSelected = async () => {
    if (selectedImages.size === 0) {
      toast.error('No images selected');
      return;
    }

    setBulkEnhancing(true);
    try {
      const imagesToEnhance = filteredImages.filter(img => selectedImages.has(img.id));
      
      const { data, error } = await supabase.functions.invoke('enhance-bulk-images', {
        body: {
          imageIds: Array.from(selectedImages),
          enhancementType: 'both',
          dpiTarget: 300
        }
      });

      if (error) throw error;

      toast.success(`Enhanced ${data.succeeded} of ${data.processed} images`);
      setSelectedImages(new Set());
      await fetchImages(true);
    } catch (error: any) {
      toast.error(error.message || 'Failed to enhance images');
    } finally {
      setBulkEnhancing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardContent className="p-6">
          <div className="text-center text-gray-400 py-12">
            <ImageIcon className="w-12 h-12 mx-auto mb-4" />
            <p className="text-white">Please sign in to view your gallery</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardContent className="p-6">
          <div className="text-center text-gray-400 py-12">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" />
            <p className="text-white">Loading your gallery...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <CardTitle className="text-white flex items-center">
            <ImageIcon className="w-6 h-6 mr-2" />
            Your Complete Gallery ({filteredImages.length} images)
          </CardTitle>
          
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {selectedImages.size > 0 && (
              <Button
                onClick={enhanceSelected}
                disabled={bulkEnhancing}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 w-full sm:w-auto"
              >
                {bulkEnhancing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enhancing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Enhance {selectedImages.size} Image(s)
                  </>
                )}
              </Button>
            )}
            
            <Button
              onClick={refreshImages}
              disabled={refreshing}
              className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            
            {filteredImages.length > 0 && (
              <>
                <Button
                  onClick={downloadAllImagesIndividually}
                  disabled={isDownloadingIndividual}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 w-full sm:w-auto"
                >
                  {isDownloadingIndividual ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download All ({filteredImages.length})
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={downloadAllImagesAsZip}
                  disabled={isDownloadingZip}
                  className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 w-full sm:w-auto"
                >
                  {isDownloadingZip ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating ZIP...
                    </>
                  ) : (
                    <>
                      <Archive className="w-4 h-4 mr-2" />
                      Download as ZIP
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-4 mt-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search prompts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
          </div>
          
          <Select value={modelFilter} onValueChange={setModelFilter}>
            <SelectTrigger className="w-full lg:w-48 bg-white/10 border-white/20 text-white">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Models</SelectItem>
              <SelectItem value="dall-e-2">DALL-E 2</SelectItem>
              <SelectItem value="dall-e-3">DALL-E 3</SelectItem>
              <SelectItem value="provider-1/FLUX.1-schnell">FLUX.1 Schnell</SelectItem>
              <SelectItem value="provider-2/FLUX.1-schnell">FLUX.1 Schnell (P2)</SelectItem>
              <SelectItem value="provider-3/FLUX.1-schnell">FLUX.1 Schnell (P3)</SelectItem>
              <SelectItem value="provider-5/FLUX.1 [schnell]">FLUX.1 Schnell (P5)</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full lg:w-32 bg-white/10 border-white/20 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      
      <CardContent className="p-4 sm:p-6">
        {filteredImages.length === 0 ? (
          <div className="text-center text-gray-400 py-12">
            <ImageIcon className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Images Found</h3>
            <p className="text-gray-300">
              {searchTerm || modelFilter !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Start generating your first images to see them here'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
              {filteredImages.map((image) => (
                <div key={image.id} className="relative group">
                  <div className="bg-white/5 rounded-xl overflow-hidden hover:bg-white/10 transition-all duration-300 border border-white/10 hover:border-white/20">
                    {/* Selection Checkbox */}
                    <div className="absolute top-2 left-2 z-10">
                      <input
                        type="checkbox"
                        checked={selectedImages.has(image.id)}
                        onChange={() => toggleImageSelection(image.id)}
                        className="w-5 h-5 cursor-pointer"
                      />
                    </div>
                    
                    {/* Enhancement Badge */}
                    {image.is_enhanced && (
                      <div className="absolute top-2 right-2 z-10 bg-purple-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Enhanced
                      </div>
                    )}
                    
                    <div className="relative aspect-square">
                        <img
                          src={image.image_url || (image.b64_json ? `data:image/png;base64,${image.b64_json}` : '')}
                          alt={image.prompt}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzM3NDE0OSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5Q0E0QUYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgRm91bmQ8L3RleHQ+PC9zdmc+';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <ImagePopup 
                          image={image} 
                          onCopySettings={onCopySettings || (() => {})}
                        >
                          <Button size="sm" className="bg-white/20 hover:bg-white/30 text-white text-xs px-3 py-1.5">
                            <Eye className="w-3 h-3" />
                          </Button>
                        </ImagePopup>
                        <Button
                          size="sm"
                          onClick={() => downloadImage(image.image_url, image.prompt)}
                          className="bg-white/20 hover:bg-white/30 text-white text-xs px-3 py-1.5"
                        >
                          <Download className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-white text-xs mb-2 line-clamp-2 leading-relaxed">
                        {image.prompt.length > 100 ? `${image.prompt.slice(0, 100)}...` : image.prompt}
                      </p>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-blue-400 font-medium truncate mr-2">
                          {image.model.replace('provider-', 'P').replace('/', ' ')}
                        </span>
                        <span className="text-gray-400">{image.size}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400 truncate mr-2">{formatDate(image.created_at)}</span>
                        <span className="text-gray-400 capitalize">{image.quality}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {hasMore && (
              <div className="text-center mt-8">
                <Button
                  onClick={loadMore}
                  disabled={loadingMore}
                  variant="outline"
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                >
                  {loadingMore ? (
                    <>
                      <div className="custom-loader w-4 h-4 mr-2" style={{width: '16px', height: '32px'}}></div>
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AllGeneratedImages;
